/* eslint-disable react-hooks/rules-of-hooks */
import d3Cloud from 'd3-cloud';
import { select } from 'd3-selection';
import 'd3-transition';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';

import debounce from 'lodash/debounce';
import max from 'lodash/max';
import min from 'lodash/min';
import throttle from 'lodash/throttle';

import { FontType, FontWeight } from '../../../../constants/Fonts.js';
import * as S from '../WordCloud.styles.js';

const WORDCLOUD_COLORS = [
  // Rainbow Dash
  '#ee4035',
  '#f37736',
  '#7bc043',
  '#0392cf',
  // Google Colors
  '#008744',
  '#d62d20',
  '#ffa700',
  // Metro UI Colors
  '#d11141',
  '#00b159',
  '#00aedb',
  '#f37735',
  '#ffc425',
  // https://www.color-hex.com/color-palette/33554
  '#0078ff',
  '#bd00ff',
  '#ff9a00',
  // Brainstorm colors
  '#fd2562',
  '#ff8f00',
  '#d568df',
  '#5fc0b7',
  '#ff3600',
  '#72df68',
  '#21cbff',
  '#ef4a91',
  '#1f77b4',
  '#ff7f0e',
  '#ff99b4',
  '#2ca02c',
  '#d62728',
  '#9467bd',
  '#e377c2',
  '#6eeb83',
  '#17becf',
  // https://www.color-hex.com/color-palette/79652
  '#ff00ff',
  '#f00',
  // https://www.color-hex.com/color-palette/79641
  '#ec008c',
  '#25aae1',
  // https://www.color-hex.com/color-palette/79592
  '#16c2ef',
  '#24f5a2',
  '#f51037',
  // https://www.color-hex.com/color-palette/79360
  '#ff924c',
  '#4cb9ff',
  '#4c60ff',
];

function getMultiplier(width, height) {
  const multipliers = [
    // 4:3
    [640, 480, 4],
    [800, 600, 3],
    [1024, 768, 2.2],
    [1440, 1080, 2],
    // 16:9
    [1280, 720, 2.5],
    [1366, 768, 2.7],
    [1920, 1080, 1.9],
    // 16:10
    [1280, 800, 2],
    [1440, 900, 2],
    [1920, 1200, 2],
  ];

  const distances = multipliers.map(([w, h, multiplier]) => ({
    distance: Math.abs(width - w) + Math.abs(height - h),
    multiplier,
    h,
    w,
  }));

  const minDistance = Math.min(...distances.map(({ distance }) => distance));

  return distances.find(({ distance }) => distance === minDistance);
}

const FONT_NAME = FontType.PRIMARY;
const FONT_WEIGHT = FontWeight.BLACK;

export const WordCloud = ({ updateId, words }) => {
  if (!words) {
    return null;
  }
  const _wrapper = useRef(null);
  const [height, setHeight] = useState(null);
  const [width, setWidth] = useState(null);
  const _g = useRef(null);
  const firstRender = useRef(true);
  let layoutStarted = false;
  const hasCloud = useRef(false);
  let layout = null;
  let _colorIdx = 0;

  const interpolateFunction = ({ xMin, xMax, yMin, yMax }) => {
    const a = (yMax - yMin) / (xMax - xMin);
    const b = (xMax * yMin - xMin * yMax) / (xMax - xMin);

    return x => a * x + b;
  };

  const wordToColor = () =>
    WORDCLOUD_COLORS[_colorIdx++ % WORDCLOUD_COLORS.length];

  const zoomToFit = () => {
    if (words.length > 0) {
      const xMin = min(words.map(w => w.x - w.width / 2));
      const xMax = max(words.map(w => w.x + w.width / 2));
      const yMin = min(words.map(w => w.y - w.height / 2));
      const yMax = max(words.map(w => w.y + w.height / 2));
      const eleWidth = Math.abs(xMax - xMin);
      const eleHeight = Math.abs(yMax - yMin);
      const midX = xMin + eleWidth / 2;
      const midY = yMin + eleHeight / 2;
      const scale = 1 / Math.max(eleWidth / width, eleHeight / height);
      const translate = [width / 2 - scale * midX, height / 2 - scale * midY];

      select(_g.current)
        .transition()
        .duration(500)
        .attr(
          'transform',
          ` translate(${translate[0]}, ${translate[1]}) scale(${scale})`
        );
    }
  };

  const initCloud = wordsBis => {
    select(_g.current)
      .selectAll('text')
      .data(wordsBis)
      .enter()
      .append('text')
      .transition()
      .duration(w => w.time)
      .attr('opacity', 1)
      .style('font-size', w => `${w.size}px`)
      .style('font-family', FONT_NAME)
      .style('font-weight', FONT_WEIGHT)
      .style('fill', w => wordToColor(w.word))
      .attr('class', w => `wordcloud-${wordToColor(w.word).replace('#', '')}`)
      .attr('text-anchor', 'middle')
      .attr('transform', w => `rotate(${w.rotate})`)
      .attr('transform', w => `translate(${[w.x, w.y]}) rotate(${w.rotate})`)
      .text(w => w.word);

    hasCloud.current = true;
  };

  const updateCloud = data => {
    layoutStarted = false;
    if (!hasCloud.current) {
      initCloud(data);
    } else {
      const wordsBis = select(_g.current).selectAll('text').data(data);

      wordsBis.transition();

      wordsBis
        .transition()
        .duration(w => w.time)
        .attr('opacity', 1)
        .style('font-size', w => `${w.size}px`)
        .style('font-family', FONT_NAME)
        .style('font-weight', FONT_WEIGHT)
        .style('fill', w => wordToColor(w.word))
        .attr('class', w => `wordcloud-${wordToColor(w.word).replace('#', '')}`)
        .attr('text-anchor', 'middle')
        .attr('transform', w => `rotate(${w.rotate})`)
        .attr('transform', w => `translate(${[w.x, w.y]}) rotate(${w.rotate})`)
        .text(w => w.word);

      wordsBis
        .enter()
        .append('text')
        .attr('text-anchor', 'middle')
        .attr('transform', w => `translate(${[w.x, w.y]}) rotate(${w.rotate})`)
        .style('font-size', w => `${w.size}px`)
        .style('font-family', FONT_NAME)
        .style('font-weight', FONT_WEIGHT)
        .style('fill', w => wordToColor(w.word))
        .attr('class', w => `wordcloud-${wordToColor(w.word).replace('#', '')}`)
        .text(w => w.word);

      wordsBis
        .style('font-family', FONT_NAME)
        .style('font-weight', FONT_WEIGHT)
        .style('fill', w => wordToColor(w.word))
        .attr('class', w => `wordcloud-${wordToColor(w.word).replace('#', '')}`)
        .text(w => w.word);

      wordsBis.exit().remove();
    }

    zoomToFit();
  };

  let layoutWords = () => {
    if (layoutStarted) {
      return;
    }

    layoutStarted = true;

    for (let t = 0; t < words.length; t++) {
      // The prop `time` doesn't exist initially, but we add it here.
      //eslint-disable-next-line react/prop-types
      words[t].time = 10 * t + 500;
    }

    const fn = interpolateFunction({
      xMin: 0,
      xMax: max(words.map(w => w.frequency)),
      yMin: 20,
      yMax: 95,
    });

    const { multiplier } = getMultiplier(window.innerWidth, window.innerHeight);

    // Don't try to layout words if one of the dimension is zero. If we don't do
    // this, it could crash because the algorithm won't be able to compute the
    // size of words
    if (height && width) {
      layout = d3Cloud()
        .size([width * multiplier, height * multiplier])
        .words(words)
        .font(FONT_NAME)
        .fontSize(w => fn(w.frequency))
        .fontWeight(FONT_WEIGHT)
        .padding(5)
        .rotate(0)
        .random(() => 0)
        .text(w => w.word)
        .on('end', updateCloud);

      layout.start();
    }
  };

  let onResize = () => {
    if (_wrapper) {
      const { height, width } = _wrapper.current.getBoundingClientRect();
      setWidth(width);
      setHeight(height);
    }
  };

  // eslint-disable-next-line react-hooks/rules-of-hooks
  useEffect(() => {
    _colorIdx = 0;
    layoutWords();
  }, [updateId, width, height]);

  useEffect(() => {
    if (firstRender.current) {
      onResize();
      firstRender.current = false;
    }
    //TODO: cancel layoutWords when called multiple times (but don't block it though)
    layoutWords = throttle(layoutWords, 500);
    onResize = debounce(onResize, 300);

    window.addEventListener('resize', onResize);

    // This colour index is used to change the colour for each word. We increment
    // this index each time we get a colour.
    _colorIdx = 0;
    return () => {
      window.removeEventListener('resize', onResize);

      if (layout && layoutStarted) {
        layout.stop();
      }
    };
  });

  return (
    <S.WordcloudWrapper ref={_wrapper}>
      <svg
        height={height}
        preserveAspectRatio='xMidYMid meet'
        viewBox={`0 0 ${width} ${height}`}
        width='100%'
      >
        <g ref={_g}>
          <noscript>
            {words.map((word, index) => (
              <p key={`${word}-${index}`}>{word.word}</p>
            ))}
          </noscript>
        </g>
      </svg>
    </S.WordcloudWrapper>
  );
};

WordCloud.propTypes = {
  /**
   * Content of the HeaderBasic slice.
   */
  updateId: PropTypes.number,
  words: PropTypes.arrayOf(
    PropTypes.shape({
      frequency: PropTypes.number.isRequired,
      word: PropTypes.string.isRequired,
    })
  ).isRequired,
};
